/**
 * Integration Accounts Modal
 * Shared connected-account manager (Google + Microsoft) rendered from the
 * calendar, drive, and generic module surfaces. Lists connected accounts with
 * a provider-agnostic full disconnect (remove vs retain) and an "Add Account"
 * button. With 2+ accounts: shows the primary badge / "Make Primary" to
 * designate the write account.
 */

import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useSession } from '@/features/auth/hooks/useSession';
import { apiFetch } from '@/lib/apiClient';
import { deleteIntegration } from '@/lib/api/endpoints/integrations';
import { broadcastIntegrationDisconnect } from '@/modules/shared/integrationAccounts';
import { connectProvider } from '@/features/oauth/lib/connect-provider';
import { useEscapeKey } from '@/common/hooks/useEscapeKey';
import { useModalOverlayClick } from '@/common/hooks/useModalOverlayClick';
import {
  IntegrationSyncStatusBadge,
  type IntegrationSyncErrorCode,
} from '@/common/components/IntegrationSyncStatusBadge';

interface IntegrationAccount {
  id: string;
  email: string;
  provider: string;
  isPrimary?: boolean;
  enabledModules?: string[];
  /** 'disabled' = Retain-disconnected (data kept, not syncing). */
  status?: 'active' | 'disabled';
  // Sync error state surfaced from the backend (cleared on next successful sync).
  syncError?: string | null;
  syncErrorCode?: IntegrationSyncErrorCode | null;
  syncErrorAt?: string | null;
}

interface IntegrationAccountsModalProps {
  onClose: () => void;
  moduleId?: string;
}

export function IntegrationAccountsModal({ onClose, moduleId }: IntegrationAccountsModalProps) {
  const { token } = useSession();
  const queryClient = useQueryClient();
  const [accounts, setAccounts] = useState<IntegrationAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [disconnecting, setDisconnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [starHoverLocked, setStarHoverLocked] = useState(false);
  const [disconnectConfirmation, setDisconnectConfirmation] = useState<{ accountId: string; email: string } | null>(null);

  const scopeQuery = moduleId ? `?moduleId=${encodeURIComponent(moduleId)}` : '';

  useEscapeKey(onClose);
  const [modalContentRef, handleOverlayClick, handleOverlayMouseDown] = useModalOverlayClick(onClose);

  const fetchAccounts = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const data = await apiFetch<{ accounts: Array<Omit<IntegrationAccount, 'provider'> & { provider?: string }> }>(
        `/integrations/calendar/accounts${scopeQuery}`,
        { authToken: token }
      );
      setAccounts(data.accounts.map(acc => ({
        ...acc,
        provider: acc.provider ?? 'google',
      })));
    } catch (err) {
      console.error('Failed to fetch accounts:', err);
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  }, [token, scopeQuery]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const handleDisconnect = async (accountId: string) => {
    const account = accounts.find(a => a.id === accountId);
    setDisconnectConfirmation({ accountId, email: account?.email || 'this account' });
  };

  const confirmDisconnect = async (mode: 'remove' | 'retain') => {
    if (!disconnectConfirmation || !token) return;
    const { accountId } = disconnectConfirmation;
    setDisconnecting(true);
    setError(null);
    setDisconnectConfirmation(null);
    try {
      // Provider-agnostic full disconnect (#3756 D4 / #3999) via the canonical
      // workspace-scoped cascade (`DELETE /workspaces/:ws/integrations/:id`):
      // purges synced data + module links + acl_grants and revokes tokens for
      // BOTH Google and Microsoft. `mode` preserves remove (purge) vs retain
      // (soft-disable, keep data read-only; #3226) for every provider. Same
      // path the calendar hook + ManageAccountsModal already use (apps/api
      // integration-disconnect.handler.ts). Microsoft previously got a degraded
      // module-unlink only; now both providers are first-class.
      await deleteIntegration(token, accountId, mode);
      // Broadcast the disconnect + invalidate the shared cross-module account
      // caches so every pane clears stale rows (#2722).
      broadcastIntegrationDisconnect(queryClient, accountId);
      await fetchAccounts();
    } catch (err) {
      console.error('Failed to disconnect:', err);
      setError('Failed to disconnect account.');
    } finally {
      setDisconnecting(false);
    }
  };

  // Hover/focus reveal of the inline "Disconnect" label — keyboard focus gets
  // the same affordance as the mouse (a11y parity with ManageAccountsModal).
  const revealDisconnectLabel = (btn: HTMLElement) => {
    if (disconnecting) return;
    btn.style.color = '#dc2626';
    const label = btn.querySelector('span') as HTMLElement | null;
    if (label) { label.style.maxWidth = '80px'; label.style.marginLeft = '4px'; }
  };
  const collapseDisconnectLabel = (btn: HTMLElement) => {
    btn.style.color = 'var(--color-text-muted)';
    const label = btn.querySelector('span') as HTMLElement | null;
    if (label) { label.style.maxWidth = '0'; label.style.marginLeft = '0'; }
  };

  const handleSetPrimary = async (accountId: string) => {
    if (!token) return;
    const targetModuleId = moduleId || accounts.find(a => a.id === accountId)?.enabledModules?.[0];
    if (!targetModuleId) return;
    // Clear any lingering hover styles (star hover hides disconnect via inline style)
    document.querySelectorAll('[data-disconnect-btn]').forEach((el) => {
      (el as HTMLElement).style.opacity = '';
      (el as HTMLElement).style.pointerEvents = '';
    });
    document.querySelectorAll('[data-email-wrap]').forEach((el) => {
      (el as HTMLElement).style.marginLeft = '';
    });

    try {
      await apiFetch(`/integrations/google/${accountId}/modules/${targetModuleId}/set-primary`, {
        method: 'POST',
        authToken: token,
      });

      // Lock star hover for 800ms so the swap animation plays without
      // the other account's star immediately triggering on mouse position
      setStarHoverLocked(true);
      setTimeout(() => setStarHoverLocked(false), 800);

      // FLIP animation: capture current positions before reorder
      const rows = document.querySelectorAll('[data-account-row]');
      const firstPositions = new Map<string, DOMRect>();
      rows.forEach((row) => {
        const id = row.getAttribute('data-account-id');
        if (id) firstPositions.set(id, row.getBoundingClientRect());
      });

      // Refetch to sync with server, keep primary-first order
      const data = await apiFetch<{ accounts: Array<Omit<IntegrationAccount, 'provider'> & { provider?: string }> }>(
        `/integrations/calendar/accounts${scopeQuery}`,
        { authToken: token },
      );
      // Sort: primary first, rest by original position
      const sorted = [...data.accounts].sort((a, b) => {
        if (a.isPrimary && !b.isPrimary) return -1;
        if (!a.isPrimary && b.isPrimary) return 1;
        return 0;
      }).map(acc => ({ ...acc, provider: acc.provider ?? 'google' }));
      setAccounts(sorted);

      // After React renders, animate from old positions to new
      requestAnimationFrame(() => {
        const newRows = document.querySelectorAll('[data-account-row]');
        newRows.forEach((row) => {
          const id = row.getAttribute('data-account-id');
          if (!id) return;
          const first = firstPositions.get(id);
          if (!first) return;
          const last = row.getBoundingClientRect();
          const deltaY = first.top - last.top;
          if (Math.abs(deltaY) < 1) return;
          const el = row as HTMLElement;
          el.style.transform = `translateY(${deltaY}px)`;
          el.style.transition = 'none';
          requestAnimationFrame(() => {
            el.style.transition = 'transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)';
            el.style.transform = 'translateY(0)';
            // Clean up after animation
            const cleanup = () => { el.style.transform = ''; el.style.transition = ''; el.removeEventListener('transitionend', cleanup); };
            el.addEventListener('transitionend', cleanup);
          });
        });
      });
    } catch (err) {
      console.error('Failed to set primary account:', err);
      setError('Failed to set primary account.');
    }
  };

  const handleAddAccount = () => {
    // Skip the intro modal — go straight to Google OAuth so adding an
    // additional account feels instant rather than routing through a
    // "Connect to Google" confirmation screen the user already saw.
    window.dispatchEvent(new Event('triggerGoogleOAuth'));
    onClose();
  };

  const showPrimary = accounts.length >= 2;
  // If none is marked primary, treat the first account as the default primary
  const hasPrimary = accounts.some(a => a.isPrimary);

  return (<>
    {createPortal(
    <div
      onClick={handleOverlayClick}
      onMouseDown={handleOverlayMouseDown}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
      }}
    >
      <div
        ref={modalContentRef as React.RefObject<HTMLDivElement>}
        style={{
          background: 'var(--color-surface, white)',
          borderRadius: '12px',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
          maxWidth: '400px',
          width: '100%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid var(--color-border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {accounts.length > 0 && accounts.every(a => a.provider === 'google') && (
              <img src="/google-logo.svg" width="24" height="24" alt="Google" />
            )}
            <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: 'var(--color-text-heading)' }}>
              Manage Calendar Accounts
            </h2>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              fontSize: '24px',
              lineHeight: 1,
              color: 'var(--color-text-muted)',
            }}
          >
            &times;
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {error && (
            <p style={{ fontSize: '12px', color: '#dc2626', margin: 0 }}>{error}</p>
          )}

          {loading ? (
            <div style={{ fontSize: '14px', color: 'var(--color-text-faint)', padding: '12px 0', textAlign: 'center' }}>
              Loading accounts...
            </div>
          ) : accounts.length > 0 ? (
            <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {accounts.map((account, idx) => {
                // Exactly one account is primary: either explicitly flagged,
                // or the first account as default when none is flagged yet.
                const isPrimary = hasPrimary ? account.isPrimary === true : idx === 0;
                const isDisabled = account.status === 'disabled';
                return (
                  <div
                    key={account.id}
                    data-account-row
                    data-account-id={account.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '10px 12px',
                      borderRadius: '6px',
                      border: '1px solid var(--color-border)',
                      backgroundColor: isDisabled ? 'var(--color-surface-hover, #f5f5f5)' : 'var(--color-surface)',
                      transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
                    }}
                  >
                    {/* Left: star + email */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0, overflow: 'hidden' }}>
                      {/* Star icon — left-aligned, gold filled for primary, gray outline for others */}
                      {showPrimary && !isDisabled && (
                        <button
                          onClick={(e) => { e.stopPropagation(); if (!isPrimary) handleSetPrimary(account.id); }}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: isPrimary ? 'default' : 'pointer',
                            padding: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            flexShrink: 0,
                            transition: 'color 0.15s ease',
                            color: isPrimary ? '#f59e0b' : 'var(--color-text-muted, #9ca3af)',
                            // When PRIMARY label is above the email, push star down to align with email line
                            alignSelf: isPrimary ? 'flex-end' : 'center',
                          }}
                          onMouseEnter={(e) => {
                            if (isPrimary || starHoverLocked) return;
                            e.currentTarget.style.color = '#f59e0b';
                            const row = e.currentTarget.closest('[data-account-row]');
                            // Push email container to right via margin-left auto
                            const emailWrap = row?.querySelector('[data-email-wrap]') as HTMLElement;
                            if (emailWrap) { emailWrap.style.marginLeft = 'auto'; }
                            // Expand "Set as primary" text
                            const setLabel = e.currentTarget.querySelector('[data-set-primary-label]') as HTMLElement;
                            if (setLabel) { setLabel.style.maxWidth = '140px'; setLabel.style.marginLeft = '8px'; }
                            // Hide disconnect button
                            const dcBtn = row?.querySelector('[data-disconnect-btn]') as HTMLElement;
                            if (dcBtn) { dcBtn.style.opacity = '0'; dcBtn.style.pointerEvents = 'none'; }
                          }}
                          onMouseLeave={(e) => {
                            if (isPrimary) return;
                            e.currentTarget.style.color = 'var(--color-text-muted, #9ca3af)';
                            const row = e.currentTarget.closest('[data-account-row]');
                            // Restore email position
                            const emailWrap = row?.querySelector('[data-email-wrap]') as HTMLElement;
                            if (emailWrap) { emailWrap.style.marginLeft = ''; }
                            // Collapse "Set as primary" text
                            const setLabel = e.currentTarget.querySelector('[data-set-primary-label]') as HTMLElement;
                            if (setLabel) { setLabel.style.maxWidth = '0'; setLabel.style.marginLeft = '0'; }
                            // Show disconnect button
                            const dcBtn = row?.querySelector('[data-disconnect-btn]') as HTMLElement;
                            if (dcBtn) { dcBtn.style.opacity = ''; dcBtn.style.pointerEvents = ''; }
                          }}
                          title={isPrimary ? 'Primary account' : 'Set as primary'}
                          aria-label={isPrimary ? `${account.email} is the primary account` : `Set ${account.email} as primary`}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill={isPrimary ? '#f59e0b' : 'none'} stroke={isPrimary ? '#f59e0b' : 'currentColor'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                          </svg>
                          {!isPrimary && (
                            <span
                              data-set-primary-label
                              style={{
                                fontSize: '13px',
                                fontWeight: 600,
                                color: '#d97706',
                                maxWidth: 0,
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                transition: 'max-width 0.2s ease, margin-left 0.2s ease',
                                marginLeft: 0,
                              }}>
                              Set as primary
                            </span>
                          )}
                        </button>
                      )}
                      <div data-email-wrap style={{ flex: 1, minWidth: 0, transition: 'margin-left 0.2s ease' }}>
                        {showPrimary && !isDisabled && (
                          <div style={{
                            fontSize: '10px',
                            fontWeight: 600,
                            color: 'var(--color-primary, #1a9ba5)',
                            letterSpacing: '0.04em',
                            maxHeight: isPrimary ? '18px' : '0px',
                            marginBottom: isPrimary ? '2px' : '0px',
                            overflow: 'hidden',
                            opacity: isPrimary ? 1 : 0,
                            transition: 'max-height 0.3s ease, margin-bottom 0.3s ease, opacity 0.3s ease',
                          }}>
                            PRIMARY
                          </div>
                        )}
                        <span
                          data-email-label
                          style={{
                            fontSize: '14px',
                            color: isDisabled ? 'var(--color-text-muted)' : 'var(--color-text)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            display: 'block',
                          }}
                          title={account.email}
                        >
                          {account.email}
                          {account.provider && account.provider !== 'google' && (
                            <span style={{ color: 'var(--color-text-muted)', fontSize: '12px' }}>
                              {' '}({account.provider === 'microsoft' ? 'Microsoft' : account.provider})
                            </span>
                          )}
                        </span>
                        {isDisabled && (
                          <span style={{ fontSize: '11px', color: 'var(--color-text-muted)', display: 'block', marginTop: 2 }}>
                            Disconnected — data retained
                          </span>
                        )}
                        {account.syncErrorCode && (
                          <div style={{ marginTop: 2, display: 'flex' }}>
                            <IntegrationSyncStatusBadge
                              syncErrorCode={account.syncErrorCode}
                              syncError={account.syncError}
                              syncErrorAt={account.syncErrorAt}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Right: reconnect (when retained) or disconnect */}
                    {isDisabled ? (
                      <button
                        onClick={() => {
                          if (account.provider === 'microsoft') {
                            try { sessionStorage.setItem('email_oauth_in_progress', 'true'); } catch { /* best-effort */ }
                            // No integrationId → FLOW A (enroll new account): picker shows.
                            void connectProvider({ provider: 'microsoft', authToken: token })
                              .catch(() => { try { sessionStorage.removeItem('email_oauth_in_progress'); } catch { /* best-effort */ } });
                          } else {
                            window.dispatchEvent(new Event('triggerGoogleOAuth'));
                          }
                        }}
                        style={{
                          background: 'none', border: '1px solid var(--color-border)', borderRadius: '6px',
                          cursor: 'pointer', padding: '4px 10px', fontSize: '12px', fontWeight: 600,
                          color: 'var(--color-primary, #1a9ba5)', flexShrink: 0, whiteSpace: 'nowrap',
                          alignSelf: isPrimary ? 'flex-end' : 'center',
                        }}
                        title="Reconnect this account (resumes syncing; retained data stays)"
                        aria-label={`Reconnect ${account.email}`}
                      >
                        Reconnect
                      </button>
                    ) : (
                    <button
                      data-disconnect-btn
                      onClick={() => handleDisconnect(account.id)}
                      disabled={disconnecting}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: disconnecting ? 'not-allowed' : 'pointer',
                        padding: '4px',
                        color: 'var(--color-text-muted)',
                        opacity: disconnecting ? 0.5 : 1,
                        transition: 'color 0.15s ease, opacity 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        flexShrink: 0,
                        alignSelf: isPrimary ? 'flex-end' : 'center',
                      }}
                      onMouseEnter={(e) => revealDisconnectLabel(e.currentTarget)}
                      onMouseLeave={(e) => collapseDisconnectLabel(e.currentTarget)}
                      onFocus={(e) => revealDisconnectLabel(e.currentTarget)}
                      onBlur={(e) => collapseDisconnectLabel(e.currentTarget)}
                      title="Disconnect this account"
                      aria-label={`Disconnect ${account.email}`}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="8" y1="12" x2="16" y2="12" />
                      </svg>
                      <span style={{
                        fontSize: '13px',
                        fontWeight: 500,
                        maxWidth: 0,
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        transition: 'max-width 0.2s ease, margin-left 0.2s ease',
                        marginLeft: 0,
                      }}>
                        Disconnect
                      </span>
                    </button>
                    )}
                  </div>
                );
              })}
            </div>
            </>
          ) : (
            <div style={{ fontSize: '14px', color: 'var(--color-text-muted)', textAlign: 'center', padding: '12px 0' }}>
              No calendar accounts connected
            </div>
          )}

          {/* Add Account button */}
          <button
            onClick={handleAddAccount}
            style={{
              width: '100%',
              backgroundColor: 'var(--color-surface)',
              padding: '10px 12px',
              border: '1px solid var(--color-border)',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: 500,
              color: 'var(--color-text-subtle)',
              cursor: 'pointer',
              transition: 'font-weight 0.1s ease, background 0.15s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.fontWeight = '600';
              e.currentTarget.style.background = 'var(--color-surface-hover, #f5f5f5)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.fontWeight = '500';
              e.currentTarget.style.background = 'white';
            }}
          >
            Add Account
          </button>
        </div>
      </div>
    </div>,
    document.body,
    )}

    {/* Disconnect confirmation modal — matches Manage Accounts modal positioning */}
    {disconnectConfirmation && createPortal(
      <div
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10001,
        }}
        onClick={() => setDisconnectConfirmation(null)}
      >
        <div
          style={{
            background: 'var(--color-surface, white)',
            borderRadius: '12px',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
            maxWidth: '400px',
            width: '100%',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ padding: '24px 24px 8px' }}>
            <p style={{ fontSize: '16px', color: 'var(--color-text)', lineHeight: 1.6, margin: 0 }}>
              Disconnect <strong>{disconnectConfirmation.email}</strong>?
            </p>
            <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', lineHeight: 1.6, margin: '8px 0 0' }}>
              This applies to the whole account across every module (email,
              calendar, and drive). Choose what happens to data already synced.
            </p>
          </div>
          {/* Remove (default) vs Retain. Remove is emphasized per the locked spec. */}
          <div style={{ padding: '8px 24px 20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <button
              onClick={() => confirmDisconnect('remove')}
              style={{
                padding: '12px 14px', background: '#dc2626', color: 'white',
                border: 'none', borderRadius: '10px', fontSize: '14px', cursor: 'pointer',
                fontWeight: 600, textAlign: 'left', lineHeight: 1.4,
              }}
            >
              Remove all data
              <span style={{ display: 'block', fontSize: '12px', fontWeight: 400, opacity: 0.9, marginTop: 2 }}>
                Delete everything synced from this account.
              </span>
            </button>
            <button
              onClick={() => confirmDisconnect('retain')}
              style={{
                padding: '12px 14px', background: 'white', color: '#374151',
                border: '1px solid #d1d5db', borderRadius: '10px', fontSize: '14px', cursor: 'pointer',
                fontWeight: 600, textAlign: 'left', lineHeight: 1.4,
              }}
            >
              Keep data, stop syncing
              <span style={{ display: 'block', fontSize: '12px', fontWeight: 400, color: 'var(--color-text-muted)', marginTop: 2 }}>
                Keep synced items as read-only; you can reconnect later.
              </span>
            </button>
            <button
              onClick={() => setDisconnectConfirmation(null)}
              style={{
                padding: '8px 16px',
                background: 'none',
                color: 'var(--color-text-muted)',
                border: '1px solid #d1d5db',
                borderRadius: '12px',
                fontSize: '14px',
                cursor: 'pointer',
              }}
            >Cancel</button>
          </div>
        </div>
      </div>,
      document.body,
    )}
  </>);
}
